import { portionsOf } from '../../utils/portionPrices'
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { GiftIcon, HeartIcon, LanguagesIcon, SearchIcon, StarIcon, XIcon } from '../icons'
import { formatTRY } from '../../utils/money'
import { isCampaignLive } from '../../utils/campaigns'
import { stockCampaignImage } from '../../utils/campaignImage'
import { DEFAULT_ACCENT, reviewLink, type MenuStyle, type PublicCampaign, type PublicMenu } from '../../services/publicMenu'
import { MENU_LANGS, RTL, STRINGS, initialLang, rememberLang, type MenuLang } from './menuI18n'
import { CampaignPopup, dealBadge, dealPrice, type CampaignCard } from './CampaignPopup'
import { ReviewCard } from './ReviewCard'
import { QrSheetAdd, QrStepper, TableDock, useProductIndex, useQrCart, type TableMode } from './TableOrder'
import './MenuView.css'

/** Case-insensitive in the reader's own alphabet, so "çay" finds "Çay". */
const fold = (s: string, lang: MenuLang) => s.toLocaleLowerCase(lang === 'tr' ? 'tr-TR' : lang)

/**
 * Favoriler müşterinin kendi telefonunda, işletme başına: hesap yok, bir
 * sonraki gelişte aynı telefonda en üstte bekliyorlar. Telefon ya da tarayıcı
 * değişirse gider, hesapsız bir menünün verebileceği en iyi şey bu.
 */
function useFavorites(key: string) {
  const storageKey = `adissoo.fav.${key}`
  const [favs, setFavs] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) ?? '[]')
    } catch {
      return []
    }
  })
  const toggle = (id: string) => {
    setFavs((cur) => {
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
      try {
        localStorage.setItem(storageKey, JSON.stringify(next))
      } catch {
        // Depolama yoksa favori yalnızca bu açılışta kalır.
      }
      return next
    })
  }
  return { favs, toggle }
}

const FAV_ID = '__favoriler'

/**
 * The QR menu itself, what a customer's phone shows, and the same drawing
 * the owner's settings preview uses, so the two can never disagree.
 *
 * @param embedded  Drawn inside a scrolling box (the settings preview) rather
 *   than the page: the category strip and scroll tracking work against that
 *   box instead of the window.
 */
export function MenuView({
  menu,
  style,
  favoritesKey,
  embedded = false,
  table,
}: {
  menu: PublicMenu
  style: MenuStyle
  /** Kurum kodu: favoriler işletme başına ayrı tutuluyor. */
  favoritesKey: string
  embedded?: boolean
  /** Masadaki karekoddan açıldı ve QR Sipariş açık: sepet, garson, hesap. */
  table?: TableMode
}) {
  const { favs, toggle: toggleFav } = useFavorites(favoritesKey)
  /** Az önce favoriye eklenen ürün: kalp atışı ve satırın zıplaması için. */
  const [beat, setBeat] = useState<string | null>(null)
  /**
   * Basılan kalp ve o an ekrandaki yeri. Favoriler menünün en üstünde: ürün
   * eklenince (ya da çıkarılınca) o bölüm büyüyüp küçülüyor ve altındaki her
   * şey kayıyordu; gezinen müşteri yerini kaybediyordu (kullanıcı, 6 Eki
   * 2026: "gizli eklensin, yukarı çıktığımda göreyim"). Değişiklikten sonra
   * kaydırma aynı farkla düzeltiliyor, basılan kalp parmağın altında kalıyor.
   */
  const favAnchor = useRef<{ el: HTMLElement; top: number } | null>(null)
  const onFav = (id: string, el: HTMLElement) => {
    favAnchor.current = { el, top: el.getBoundingClientRect().top }
    const adding = !favs.includes(id)
    toggleFav(id)
    if (!adding) return
    setBeat(null)
    // Bir sonraki karede: aynı ürüne art arda basılınca animasyon baştan başlasın.
    requestAnimationFrame(() => setBeat(id))
    window.setTimeout(() => setBeat((b) => (b === id ? null : b)), 700)
  }
  /**
   * Dokunulan ürün: alttan açılan pencerede fotoğrafı (varsa), adı, fiyatı ve
   * açıklaması (kullanıcı, 6 Eki 2026). Açıklama listede kapalı olsa da burada
   * görünüyor.
   */
  const [sheet, setSheet] = useState<{
    id: string
    name: string
    image?: string | null
    description?: string | null
    priceKurus: number
    byWeight?: boolean
    soldOut?: boolean
    portions?: { name: string; priceKurus: number }[] | null
  } | null>(null)
  /**
   * Aşağı çekerek kapatma (kullanıcı, 6 Eki 2026). Tutamaç yok, kullanıcı
   * istemedi; çekme yine çalışıyor.
   * Yalnız panel en üstteyken (içerik kaydırılmışsa önce içerik kayar).
   * Yeterince çekilirse ya da hızlı fırlatılırsa kapanır, yoksa yerine döner.
   * Sürükleme boyunca React'e dokunulmuyor, doğrudan stil: her karede
   * yeniden çizim olmasın.
   */
  const sheetEl = useRef<HTMLDivElement>(null)
  const sheetBg = useRef<HTMLDivElement>(null)
  const drag = useRef<{ y0: number; t0: number; dy: number } | null>(null)
  const onSheetTouchStart = (e: React.TouchEvent) => {
    const el = sheetEl.current
    if (!el || el.scrollTop > 0) return
    drag.current = { y0: e.touches[0].clientY, t0: performance.now(), dy: 0 }
  }
  const onSheetTouchMove = (e: React.TouchEvent) => {
    const d = drag.current
    const el = sheetEl.current
    if (!d || !el) return
    d.dy = Math.max(0, e.touches[0].clientY - d.y0)
    el.style.transition = 'none'
    el.style.transform = `translateY(${d.dy}px)`
    if (sheetBg.current) sheetBg.current.style.opacity = String(1 - Math.min(0.6, d.dy / el.offsetHeight))
  }
  const onSheetTouchEnd = () => {
    const d = drag.current
    const el = sheetEl.current
    drag.current = null
    if (!d || !el || d.dy === 0) return
    const fast = d.dy / (performance.now() - d.t0) > 0.6
    el.style.transition = 'transform 200ms ease-out'
    if (sheetBg.current) sheetBg.current.style.transition = 'opacity 200ms ease-out'
    if (d.dy > 100 || fast) {
      el.style.transform = 'translateY(100%)'
      if (sheetBg.current) sheetBg.current.style.opacity = '0'
      window.setTimeout(() => setSheet(null), 190)
    } else {
      el.style.transform = ''
      if (sheetBg.current) sheetBg.current.style.opacity = ''
    }
  }

  useEffect(() => {
    if (!sheet) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSheet(null)
    window.addEventListener('keydown', onKey)
    // Arkadaki sayfa kaymasın.
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [sheet])
  const [lang, setLangState] = useState<MenuLang>(initialLang)
  const [picking, setPicking] = useState(false)
  const setLang = (l: MenuLang) => {
    setLangState(l)
    rememberLang(l)
    setPicking(false)
  }
  const t = STRINGS[lang]
  const review = reviewLink(style.reviewUrl)
  const cart = useQrCart(`adissoo.qr.${favoritesKey}.${table?.token ?? ''}.cart`)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState<string | null>(null)
  const root = useRef<HTMLDivElement>(null)
  const strip = useRef<HTMLDivElement>(null)
  /** Üstte yapışık arama ve kategori şeridi: atlarken yüksekliği kadar pay. */
  const bar = useRef<HTMLDivElement>(null)
  /** Şeritten kategoriye atlandı; kişi kaydırmaya başlayınca bırakılıyor. */
  const jumped = useRef(false)

  const accent = /^#[0-9a-f]{6}$/i.test(style.accent ?? '') ? style.accent! : DEFAULT_ACCENT
  const showDesc = style.descriptions !== false
  const theme = style.theme ?? 'auto'

  /**
   * Menü seçilen dilde. Çevirisi olmayan kalem Türkçe kalıyor; aramada her iki
   * ad da bulunuyor (garsonun söylediği Türkçe adla da aranabilsin).
   */
  const local = useMemo(() => {
    const tx = lang === 'tr' ? undefined : menu.translations[lang]
    return menu.categories.map((c) => ({
      ...c,
      name: tx?.[`c:${c.id}`]?.n ?? c.name,
      products: c.products.map((p) => {
        const hit = tx?.[`p:${p.id}`]
        return {
          ...p,
          trName: p.name,
          name: hit?.n ?? p.name,
          description: hit ? (hit.d ?? p.description) : p.description,
        }
      }),
    }))
  }, [menu, lang])
  /**
   * Şu an geçerli kampanyalar (gün, saat, tarih müşterinin telefonundan) ve
   * her birinin kapsadığı ürünler. Ürün başına ilk uyan kampanya etiket olarak
   * menüde de görünüyor; kasada da bir ürüne iki kampanya düşmüyor.
   */
  const cards = useMemo<CampaignCard[]>(() => {
    const now = new Date()
    return (menu.campaigns ?? [])
      .filter((c, i) => isCampaignLive({ ...c, active: true, sortOrder: i, showOnQr: true }, now))
      .map((c) => {
        const wholeMenu = c.categoryIds.length === 0 && c.productIds.length === 0
        const products = wholeMenu
          ? []
          : local.flatMap((cat) =>
              cat.products
                .filter((p) => !p.soldOut && (c.productIds.includes(p.id) || c.categoryIds.includes(cat.id)))
                .map((p) => ({ ...p, dealKurus: dealPrice(c, p.priceKurus) })),
            )
        const trNames = menu.categories.flatMap((cat) => [
          ...(c.categoryIds.includes(cat.id) ? [cat.name] : []),
          ...cat.products
            .filter((p) => c.productIds.includes(p.id) || c.categoryIds.includes(cat.id))
            .map((p) => p.name),
        ])
        return { campaign: c, wholeMenu, products, image: c.image || stockCampaignImage(trNames) }
      })
      // Kapsadığı ürünlerin hepsi tükenmişse göstermenin anlamı yok.
      .filter((card) => card.wholeMenu || card.products.length > 0)
  }, [menu, local])
  const dealOf = useMemo(() => {
    const map = new Map<string, PublicCampaign>()
    for (const card of cards) {
      const ids = card.wholeMenu ? local.flatMap((c) => c.products.map((p) => p.id)) : card.products.map((p) => p.id)
      for (const id of ids) if (!map.has(id)) map.set(id, card.campaign)
    }
    return map
  }, [cards, local])
  /** Her açılışta bir kez (kullanıcı, 16 Eki 2026); ayar önizlemesinde hiç. */
  const [promo, setPromo] = useState(() => !embedded)
  const tagline =
    (lang !== 'tr' && style.tagline?.trim() && menu.translations[lang]?.['t:tagline']?.n) ||
    style.tagline?.trim() ||
    t.menu

  const shown = useMemo(() => {
    const q = fold(query.trim(), lang)
    return local
      .map((c) => ({
        ...c,
        products: c.products.filter(
          (p) =>
            !q ||
            fold(p.name, lang).includes(q) ||
            fold(p.trName, 'tr').includes(fold(query.trim(), 'tr')) ||
            fold(p.description ?? '', lang).includes(q),
        ),
      }))
      .filter((c) => c.products.length > 0)
  }, [local, query, lang])

  /** Favoriler, menünün en üstünde kendi bölümleri olarak, aramada yok. */
  const productIndex = useProductIndex(local)
  /** Masadan eklenebilir mi: tükenmiş ve tartılı ürün garsona soruluyor. */
  const orderable = (p: { soldOut: boolean; byWeight?: boolean }) => !!table && !p.soldOut && !p.byWeight

  const sections = useMemo(() => {
    if (query.trim() || favs.length === 0) return shown
    const all = local.flatMap((c) => c.products)
    const picked = favs
      .map((id) => all.find((p) => p.id === id))
      .filter((p): p is NonNullable<typeof p> => !!p)
    if (picked.length === 0) return shown
    return [{ id: FAV_ID, name: t.favorites, sortOrder: -1, products: picked }, ...shown]
  }, [shown, favs, local, query, t])

  // Hangi kategori ekranda: şeridin işareti onu izliyor. Şeridin hemen
  // altına gelmiş son bölüm işaretli; sayfanın dibinde son bölüm (son
  // bölümler ekranın tepesine hiç çıkamıyor).
  useEffect(() => {
    const scroller = embedded ? root.current : null
    const list = root.current?.querySelectorAll<HTMLElement>('[data-cat]')
    if (!list || list.length === 0) return
    const all = [...list]
    const atEnd = () =>
      scroller
        ? scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4
        : window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4
    const pick = () => {
      // Şeritten bir kategoriye basıldı: kişi kendisi kaydırana kadar o kalsın.
      // Yoksa kısa son bölümlere atlayınca dipteki bölüm işaretleniyordu.
      if (jumped.current) return
      if (atEnd()) {
        setActive(all[all.length - 1].dataset.cat ?? null)
        return
      }
      const line = (bar.current?.getBoundingClientRect().bottom ?? 0) + 40
      let cur = all[0]
      for (const el of all) if (el.getBoundingClientRect().top <= line) cur = el
      setActive(cur.dataset.cat ?? null)
    }
    const release = () => {
      jumped.current = false
    }
    const target: HTMLElement | Window = scroller ?? window
    target.addEventListener('scroll', pick, { passive: true })
    window.addEventListener('touchstart', release, { passive: true })
    window.addEventListener('wheel', release, { passive: true })
    window.addEventListener('keydown', release)
    pick()
    return () => {
      target.removeEventListener('scroll', pick)
      window.removeEventListener('touchstart', release)
      window.removeEventListener('wheel', release)
      window.removeEventListener('keydown', release)
    }
  }, [embedded, sections])

  // İşaretli sekme şeritte görünür kalsın.
  useEffect(() => {
    if (!active) return
    const bar = strip.current
    const chip = bar?.querySelector<HTMLElement>(`[data-chip="${active}"]`)
    if (!bar || !chip) return
    // Yalnız şeridi kaydırıyor (25 Eki 2026): scrollIntoView bütün sayfayı da
    // kaydırıyordu, Ayarlar > QR Menü açılınca ekran önizlemeye iniyordu.
    const left =
      bar.scrollLeft + chip.getBoundingClientRect().left - bar.getBoundingClientRect().left - (bar.clientWidth - chip.offsetWidth) / 2
    bar.scrollTo({ left: Math.max(0, left), behavior: 'smooth' })
  }, [active])

  useLayoutEffect(() => {
    const a = favAnchor.current
    favAnchor.current = null
    // Favoriler bölümünün içinden çıkarılan ürün: düğmesi artık yok, düzeltecek
    // bir yer de yok.
    if (!a || !a.el.isConnected) return
    const shift = a.el.getBoundingClientRect().top - a.top
    if (Math.abs(shift) < 1) return
    if (embedded && root.current) root.current.scrollTop += shift
    else window.scrollTo({ top: window.scrollY + shift, behavior: 'instant' })
  }, [favs, embedded])

  const jump = (id: string) => {
    const el = root.current?.querySelector<HTMLElement>(`[data-cat="${id}"]`)
    if (!el) return
    setActive(id)
    jumped.current = true
    // Üstte yapışık duran arama ve kategori şeridinin gerçek yüksekliği kadar
    // pay: sabit 70 px şeritten kısaydı, kategori adı şeridin altında yarım
    // kalıyordu (kullanıcı, 6 Eki 2026).
    // Ölçü kategori başlığından: başlık şeridin 8 px altına oturuyor.
    const head = el.querySelector('h2') ?? el
    // Şeridin yapıştığında duracağı yer (sayfanın tepesindeyken henüz aşağıda).
    const stuck = (embedded && root.current ? root.current.getBoundingClientRect().top : 0) + (bar.current?.offsetHeight ?? 70)
    const shift = head.getBoundingClientRect().top - stuck - 8
    if (embedded && root.current) {
      root.current.scrollBy({ top: shift, behavior: 'smooth' })
    } else {
      window.scrollTo({ top: window.scrollY + shift, behavior: 'smooth' })
    }
  }

  return (
    <div
      ref={root}
      className={`pm pm--${theme} ${embedded ? 'pm--embedded' : ''} ${table ? 'pm--table' : ''}`}
      style={{ '--pm-accent': accent } as CSSProperties}
      dir={RTL.includes(lang) ? 'rtl' : 'ltr'}
      lang={lang}
    >
      {/* Adissoo işareti sol üstte, dil sağ üstte (Arapçada yer değiştiriyorlar). */}
      <a
        className="pm__home"
        href="https://www.adissoo.com"
        target="_blank"
        rel="noreferrer"
        aria-label="Adissoo"
      >
        <img src="/logo/logo.svg" alt="" width={32} height={32} />
      </a>
      {/* Dil: sağ üst köşede (Arapçada sol üst, sayfa aynalanıyor). */}
      <div className="pm__lang">
        <button
          type="button"
          className="pm__lang-btn"
          onClick={() => setPicking((v) => !v)}
          aria-expanded={picking}
          aria-label={t.language}
        >
          <LanguagesIcon />
          <span>{lang.toUpperCase()}</span>
        </button>
        {picking && (
          <>
            <div className="pm__lang-scrim" onClick={() => setPicking(false)} />
            <ul className="pm__lang-list" role="listbox" aria-label={t.language}>
              {MENU_LANGS.map((l) => (
                <li key={l.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={l.code === lang}
                    className={l.code === lang ? 'is-on' : ''}
                    onClick={() => setLang(l.code)}
                    lang={l.code}
                    dir="auto"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <header className="pm__head">
        <h1>{menu.name}</h1>
        <p className="pm__tagline">{tagline}</p>
        {table && (
          <div>
            <span className="pm__table">
              {table.info.name}
              {table.info.hall && <small>{table.info.hall}</small>}
            </span>
          </div>
        )}
        <span className="pm__rule" />
        {((cards.length > 0 && !embedded) || review) && (
          <div className="pm__head-acts">
            {cards.length > 0 && !embedded && (
              <button type="button" className="pm__promo" onClick={() => setPromo(true)}>
                <GiftIcon />
                {cards.length > 1 ? `${t.campaigns} (${cards.length})` : t.campaign}
              </button>
            )}
            {review && (
              <a className="pm__rate" href={review} target="_blank" rel="noopener noreferrer">
                <StarIcon filled />
                {t.review.short}
              </a>
            )}
          </div>
        )}
      </header>

      <div className="pm__bar" ref={bar}>
        <div className="pm__search">
          <SearchIcon />
          <input
            id="pm-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.search}
            autoComplete="off"
          />
        </div>
        {!query && sections.length > 1 && (
          <div className="pm__chips" ref={strip}>
            {sections.map((c) => (
              <button
                key={c.id}
                type="button"
                data-chip={c.id}
                className={`pm__chip ${active === c.id ? 'is-on' : ''} ${c.id === FAV_ID ? 'pm__chip--fav' : ''}`}
                onClick={() => jump(c.id)}
              >
                {c.id === FAV_ID && <HeartIcon filled />}
                {c.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {sections.length === 0 ? (
        <p className="pm__note">{query ? t.noResult : t.empty}</p>
      ) : (
        sections.map((c) => (
          <section key={c.id} className={`pm__cat ${c.id === FAV_ID ? 'pm__cat--fav' : ''}`} data-cat={c.id}>
            <h2>{c.name}</h2>
            <ul>
              {c.products.map((p) => (
                <li
                  key={p.id}
                  data-pid={p.id}
                  className={`pm__row ${p.soldOut ? 'is-soldout' : ''} ${beat === p.id ? 'is-beat' : ''}`}
                  // Satırın tamamı dokunulabilir (kalp hariç): ürün alttan açılıyor.
                  role="button"
                  tabIndex={0}
                  aria-label={p.name}
                  onClick={() => setSheet(p)}
                  onKeyDown={(e) => {
                    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault()
                      setSheet(p)
                    }
                  }}
                >
                  {p.image && (
                    <span className="pm__img">
                      <img src={p.image} alt="" loading="lazy" decoding="async" />
                    </span>
                  )}
                  <div className="pm__item">
                    <span className="pm__name">
                      {p.name}
                      {/* Sözle söyleniyor: yalnızca üstü çizili fiyat indirim sanılıyordu. */}
                      {p.soldOut && <span className="pm__soldout">{t.soldOut}</span>}
                      {!p.soldOut && dealOf.has(p.id) && (
                        <span className="pm__deal">{dealBadge(dealOf.get(p.id)!, lang)}</span>
                      )}
                    </span>
                    {showDesc && p.description && <span className="pm__desc">{p.description}</span>}
                    {table && (
                    <span className="pm__price pm__price--under">
                      {(() => {
                        const c = dealOf.get(p.id)
                        const deal = c && !p.soldOut ? dealPrice(c, p.priceKurus) : undefined
                        return deal === undefined ? (
                          formatTRY(p.priceKurus)
                        ) : (
                          <>
                            <s className="pm__was">{formatTRY(p.priceKurus)}</s>
                            {formatTRY(deal)}
                          </>
                        )
                      })()}
                      {p.byWeight && <small className="pm__kg"> / kg</small>}
                    </span>
                    )}
                  </div>
                  {table ? (
                    <div className="pm__side">
                      {orderable(p) && (
                        <QrStepper
                          qty={cart.qtyOf(p.id)}
                          name={p.name}
                          t={t.order}
                          // Porsiyonlu üründe "+" pencereyi açar: porsiyon orada seçilir.
                          onAdd={() => (p.portions?.length ? setSheet(p) : cart.add(p.id))}
                          onLess={() => cart.less(p.id)}
                        />
                      )}
                    </div>
                  ) : (
                    <>
                      <span className="pm__price">
                        {(() => {
                          const c = dealOf.get(p.id)
                          const deal = c && !p.soldOut ? dealPrice(c, p.priceKurus) : undefined
                          return deal === undefined ? (
                            formatTRY(p.priceKurus)
                          ) : (
                            <>
                              <s className="pm__was">{formatTRY(p.priceKurus)}</s>
                              {formatTRY(deal)}
                            </>
                          )
                        })()}
                        {p.byWeight && <small className="pm__kg"> / kg</small>}
                      </span>
                      <button
                        type="button"
                        className={`pm__fav ${favs.includes(p.id) ? 'is-on' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          onFav(p.id, e.currentTarget)
                        }}
                        aria-pressed={favs.includes(p.id)}
                        aria-label={favs.includes(p.id) ? t.removeFav(p.name) : t.addFav(p.name)}
                      >
                        <HeartIcon filled={favs.includes(p.id)} />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}

      {sheet && (
        <div className="pm__sheet-bg" ref={sheetBg} onClick={() => setSheet(null)}>
          <div
            className="pm__sheet"
            ref={sheetEl}
            role="dialog"
            aria-modal="true"
            aria-label={sheet.name}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onSheetTouchStart}
            onTouchMove={onSheetTouchMove}
            onTouchEnd={onSheetTouchEnd}
            onTouchCancel={onSheetTouchEnd}
          >
            <button type="button" className="pm__sheet-x" onClick={() => setSheet(null)} aria-label="Kapat">
              <XIcon />
            </button>
            {sheet.image && <img className="pm__sheet-img" src={sheet.image} alt={sheet.name} />}
            <div className="pm__sheet-body">
              <div className="pm__sheet-head">
                <h3>
                  {sheet.name}
                  {sheet.soldOut && <span className="pm__soldout">{t.soldOut}</span>}
                </h3>
                <b className="pm__sheet-price">
                  {(() => {
                    const c = dealOf.get(sheet.id)
                    const deal = c && !sheet.soldOut ? dealPrice(c, sheet.priceKurus) : undefined
                    return deal === undefined ? (
                      formatTRY(sheet.priceKurus)
                    ) : (
                      <>
                        <s className="pm__was">{formatTRY(sheet.priceKurus)}</s>
                        {formatTRY(deal)}
                      </>
                    )
                  })()}
                  {sheet.byWeight && <small> / kg</small>}
                </b>
                <button
                  type="button"
                  className={`pm__fav ${favs.includes(sheet.id) ? 'is-on' : ''}`}
                  onClick={() => {
                    // Arkadaki listede bu ürünün satırı yerinde kalsın.
                    const row = root.current?.querySelector<HTMLElement>(
                      `.pm__cat:not(.pm__cat--fav) [data-pid="${sheet.id}"]`,
                    )
                    if (row) favAnchor.current = { el: row, top: row.getBoundingClientRect().top }
                    toggleFav(sheet.id)
                  }}
                  aria-pressed={favs.includes(sheet.id)}
                  aria-label={favs.includes(sheet.id) ? t.removeFav(sheet.name) : t.addFav(sheet.name)}
                >
                  <HeartIcon filled={favs.includes(sheet.id)} />
                </button>
              </div>
              {sheet.description && <p className="pm__sheet-desc">{sheet.description}</p>}
              {table &&
                (orderable(sheet as { soldOut: boolean; byWeight?: boolean }) ? (
                  <QrSheetAdd
                    key={sheet.id}
                    priceKurus={sheet.priceKurus}
                    portions={sheet.portions?.length ? portionsOf(sheet) : undefined}
                    noteOptions={table.info.notes[sheet.id] ?? []}
                    t={t.order}
                    onAdd={(qty, note, portion) => {
                      cart.add(sheet.id, note, qty, portion)
                      setSheet(null)
                    }}
                  />
                ) : (
                  <p className="qo-ask">{t.order.askStaff}</p>
                ))}
            </div>
          </div>
        </div>
      )}

      {promo && cards.length > 0 && (
        <CampaignPopup
          cards={cards}
          lang={lang}
          onClose={() => setPromo(false)}
          onPick={(p) => {
            setPromo(false)
            setSheet(p)
          }}
        />
      )}

      {table && !embedded && (
        <TableDock
          table={table}
          t={t.order}
          cart={cart}
          products={productIndex}
          hasDeals={cards.length > 0}
          review={review ? { url: review, t: t.review } : undefined}
        />
      )}

      {review && <ReviewCard url={review} t={t.review} />}

      <footer className="pm__foot">
        <a href="https://www.adissoo.com" target="_blank" rel="noreferrer">
          Powered by <span className="pm__brand" role="img" aria-label="Adissoo" />
        </a>
      </footer>
    </div>
  )
}

